import type { FC, ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import type {
    SegmentWizardContextType,
    IFolderPredictions,
    IRule,
    IWordPredictions,
    ISelectedCountry,
} from "app/pages/segments/redesigned-wizard/segment-wizard/types";

const context = createContext<SegmentWizardContextType>(null);
export const useSegmentWizardContext = () => {
    return useContext(context);
};

interface ISegmentWizardContextProviderProps {
    children: ReactNode;
}

export const SegmentWizardContextProvider: FC<ISegmentWizardContextProviderProps> = ({ children }) => {
    const [wordPredictionsLoading, setWordPredictionsLoading] = useState<boolean>(false);
    const [wordPredictions, setWordPredictions] = useState<IWordPredictions>(undefined);
    const [folderPredictionsLoading, setFolderPredictionsLoading] = useState<boolean>(false);
    const [folderPredictions, setFolderPredictions] = useState<IFolderPredictions>(undefined);
    const [segmentRules, setSegmentRules] = useState<IRule[]>([]);
    const [segmentsPreviewLoading, setSegmentsPreviewLoading] = useState<boolean>(false);
    const [segmentsReady, setSegmentsReady] = useState<boolean>(false);
    const [selectedCountry, setSelectedCountry] = useState<ISelectedCountry>(undefined);

    const resetSegmentsWizardData = () => {
        setSegmentsReady(false);
        setWordPredictionsLoading(false);
        setSegmentsPreviewLoading(false);
        setFolderPredictionsLoading(false);
        setWordPredictions(undefined);
        setFolderPredictions(undefined);
        setSegmentRules([]);
    };

    const contextValue: SegmentWizardContextType = {
        requestWordPredictions: (wordPredictionsLoading) => setWordPredictionsLoading(wordPredictionsLoading),
        receiveWordPredictions: (wordPredictions) => setWordPredictions(wordPredictions),
        requestFolderPredictions: (folderPredictionsLoading) => setFolderPredictionsLoading(folderPredictionsLoading),
        receiveFolderPredictions: (folderPredictions) => setFolderPredictions(folderPredictions),
        setSegmentRules: (segmentRules) => setSegmentRules(segmentRules),
        requestSegmentsPreview: (segmentsPreviewLoading) => setSegmentsPreviewLoading(segmentsPreviewLoading),
        setSegmentsReady: (segmentsReady) => setSegmentsReady(segmentsReady),
        setSelectedCountry: (country) => setSelectedCountry(country),
        resetSegmentsWizardData,
        wordPredictionsLoading,
        wordPredictions,
        segmentRules,
        segmentsPreviewLoading,
        folderPredictions,
        folderPredictionsLoading,
        segmentsReady,
        selectedCountry,
    };

    return <context.Provider value={contextValue}>{children}</context.Provider>;
};
